import { useEffect, useState, useCallback } from 'react';

import { env } from '@/utils/baseUrl';

export const useWasm = () => {
  const [wasmModule, setWasmModule] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const getScriptUrl = useCallback(() => {
    return env === 'local'
      ? '/pkg/rust_wasm_simple.min.js'
      : `https://${env === 'fat' ? 'fat' : ''}static.szlcsc.com/ecp/assets/newWeb/common/pkg/rust_wasm_simple.min.js`;
  }, []);

  const loadScript = useCallback((src, forceReload = false) => {
    return new Promise((resolve, reject) => {
      // 检查脚本是否已经加载
      const existingScript = document.querySelector(`script[src="${src}"]`);

      if (existingScript) {
        if (forceReload) {
          // 移除已存在的脚本标签
          existingScript.remove();
          // 清理全局变量
          if (typeof wasm_bindgen !== 'undefined') {
            delete window.wasm_bindgen;
          }
        } else {
          resolve();
          return;
        }
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }, []);

  const loadWasm = useCallback(async () => {
    // 只在客户端加载WASM
    if (typeof window === 'undefined') {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('🦀 开始加载WASM模块...');

      // 动态加载JS脚本
      await loadScript(getScriptUrl());

      // 使用全局变量方式加载 (no-modules target)
      if (typeof wasm_bindgen === 'undefined') {
        throw new Error('WASM绑定未加载，请确保rust_wasm_simple.js已正确加载');
      }

      // 初始化WASM模块
      if (env === 'local') {
        await wasm_bindgen(`/pkg/rust_wasm_simple_bg.wasm`);
      } else {
        // 动态加载WASM模块
        await wasm_bindgen();
      }

      console.log('✅ WASM模块加载成功');

      setWasmModule({
        generate_nonce: wasm_bindgen.generate_nonce,
        generate_cid: wasm_bindgen.generate_cid,
        generate_sign: wasm_bindgen.generate_sign,
        decrypt_data: wasm_bindgen.decrypt_data,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '未知错误';
      console.error('WASM模块加载失败:', errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [loadScript, getScriptUrl]);

  const reload = useCallback(() => {
    return new Promise((resolve, reject) => {
      setWasmModule(null);
      setIsLoading(true);
      setError(null);

      // 强制重新加载脚本
      loadScript(getScriptUrl(), true)
        .then(async () => {
          try {
            await loadWasm();
            resolve();
          } catch (err) {
            reject(err);
          }
        })
        .catch((err) => {
          setError(err.message);
          setIsLoading(false);
          reject(err);
        });
    });
  }, [loadScript, loadWasm, getScriptUrl]);

  useEffect(() => {
    loadWasm();
  }, [loadWasm]);

  return {
    wasmModule,
    isLoading,
    error,
    reload,
  };
};
